import _ from 'lodash';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';

import { config } from '../../../config';

import { TEST_SESSION } from '../../../core/graphql/test-sessions';
import ExpiredComponent from '../../components/expired-component';
import WrongBrowser from '../../components/wrong-browser';
import withIntercom from '../../../core/components/with-intercom';
import Devices from '../../../core/constants/device-types';
import RecorderTypes from '../../../core/constants/recorder-type';

import {
  Container,
  ErrorHandler,
  Loader,
} from './styles';
import Mobile from './mobile';
import Desktop from './desktop';
import ChromeExtension from './chrome-extension';
import versionCheck from '../../../core/services/version-check';

const MAC_URL_DOWNLOAD = config.REACT_APP_MAC_URL_DOWNLOAD;
const WINDOWS_URL_DOWNLOAD = config.REACT_APP_WINDOWS_URL_DOWNLOAD;
const WINDOWS_MS_URL_DOWNLOAD = config.REACT_APP_WINDOWS_MS_STORE_URL;
const POLL_INTERVAL = 5000; // 5 seconds.

// eslint-disable-next-line consistent-return
const UnmoderatedSession = (props) => {
  /** props */
  const { location, history } = props;

  /** query string */
  const { 'session-id': id } = queryString.parse(location.search);
  const hasSession = id && !_.isEmpty(id);

  /** states */
  const [uri, setUri] = useState();
  const [expired, setExpired] = useState(false);
  const [hasExtensionInstalled, setHasExtensionInstalled] = useState(false);
  const [hasMinVersion, setHasMinVersion] = useState(true);

  /** graphql */
  const {
    data = {},
    error,
    loading,
  } = useQuery(TEST_SESSION, {
    variables: { id },
    // Added poll interval because we need to go back to the browse test when session starts
    pollInterval: POLL_INTERVAL,
    skip: !hasSession,
  });

  const { testSession = {} } = data;
  const {
    authenticationToken: token,
    expiresAt,
    recorderStarted,
    practiceTestStatus,
    tester: {
      user: {
        tester,
        guest,
      } = {},
      project: {
        participantsSource,
      } = {},
      segment: {
        device,
        recorderType,
      } = {},
    } = {},
  } = testSession;

  const isPlaybook = participantsSource === 'playbook' && tester && !guest;
  const time = isPlaybook && !practiceTestStatus ? expiresAt : null;

  /** methods */
  const openMagicLink = () => {
    window.location.href = uri;
  };

  const handleLaunchTestButtonClicked = () => {
    openMagicLink();
  };

  const handleMacDownloadClicked = () => {
    window.location.href = MAC_URL_DOWNLOAD;
  };

  const handleWindowsDownloadClicked = () => {
    if (window.navigator.platform === 'Win32' || window.navigator.platform === 'Windows' || window.navigator.platform === 'Win64') {
      window.location.href = WINDOWS_MS_URL_DOWNLOAD;
    } else {
      window.location.href = WINDOWS_URL_DOWNLOAD;
    }
  };

  const handleClickBack = () => {
    history.push('/');
    setExpired(false);
  };

  const handleSetExpired = (value) => {
    setExpired(value);
  };

  const checkExtensionVersion = async (appVersion) => {
    const { data: versionData } = await versionCheck();
    const { chrome_extension: chromeExtension } = versionData;
    if (chromeExtension !== appVersion) {
      setHasMinVersion(false);
    }
    return versionData;
  };

  const handleHasExtensionInstalled = (e) => {
    const { detail } = e;
    const { version: appVersion } = detail;
    checkExtensionVersion(appVersion);
    setHasExtensionInstalled(true);
  };

  const isChromeBrowser = () => {
    const { userAgent } = navigator;
    let browserName;

    if (userAgent.match(/edg/i)) {
      browserName = 'edge';
    } else if (userAgent.match(/opr/i)) {
      browserName = 'opera';
    } else if (userAgent.match(/firefox|fxios/i)) {
      browserName = 'firefox';
    } else if (userAgent.match(/chrome|chromium|crios/i)) {
      browserName = 'chrome';
    } else if (userAgent.match(/safari/i)) {
      browserName = 'safari';
    } else {
      browserName = 'No browser detection';
    }

    return browserName === 'chrome';
  };

  useEffect(() => {
    document.addEventListener('hasExtensionInstalled', handleHasExtensionInstalled);

    return () => {
      document.removeEventListener('hasExtensionInstalled', handleHasExtensionInstalled);
    };
  }, [loading]);

  /** lifecycle methods */
  useEffect(() => {
    // if screen recorder starts, redirect to browse tests tab
    if (recorderStarted && isPlaybook && recorderType !== RecorderTypes.CHROME_EXTENSION) {
      history.push('/');
      return;
    }

    if (hasSession && token && !uri) {
      setUri(`playbookux://${id}:${token}`);
    }
  }, [hasSession, history, id, isPlaybook, recorderStarted, recorderType, token, uri]);

  if (error) {
    return (
      <ErrorHandler
        error={error}
        defaultMessage="Error while retrieving Test Session."
      />
    );
  }

  if (loading) {
    return (
      <Container>
        <Loader data-loader="unmoderated-loader" />
      </Container>
    );
  }

  if (expired) {
    return <ExpiredComponent onClick={handleClickBack} />;
  }

  const isComputer = device === Devices.COMPUTER;

  if (isComputer && recorderType === RecorderTypes.CHROME_EXTENSION) {
    if (!isChromeBrowser()) {
      return (
        <WrongBrowser />
      );
    }

    return (
      <ChromeExtension
        uri={uri}
        sessionId={id}
        expiresAt={expiresAt}
        time={time}
        history={history}
        hasExtensionInstalled={hasExtensionInstalled}
        onSetExpired={handleSetExpired}
        hasMinVersion={hasMinVersion}
      />
    );
  }

  if (isComputer) {
    return (
      <Desktop
        uri={uri}
        onMacDownloadClick={handleMacDownloadClicked}
        onWindowsDownloadClick={handleWindowsDownloadClicked}
        onLaunchTestButtonClick={handleLaunchTestButtonClicked}
        expiresAt={time}
        onSetExpired={handleSetExpired}
      />
    );
  }

  if (!isComputer) {
    return (
      <Mobile
        uri={uri}
        expiresAt={time}
        onSetExpired={handleSetExpired}
      />
    );
  }
};

UnmoderatedSession.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
};

export default withIntercom(UnmoderatedSession);
